import { api } from "@/lib/api/client";
import type { components } from "@/types/api";

// ---------------------------------------------------------------------------
// Types from OpenAPI spec
// ---------------------------------------------------------------------------

export type App = components["schemas"]["App"];
export type CollaboratorMap = components["schemas"]["CollaboratorMap"];
export type CollaboratorMapResponse = components["schemas"]["CollaboratorMapResponse"];
export type AssignableRole = components["schemas"]["CollaboratorUpdateRequest"]["permission"];
export type AppCreationRequest = components["schemas"]["AppCreationRequest"];
export type AppTransfer = components["schemas"]["AppTransfer"];
export type AppTransferWarnings = components["schemas"]["AppTransferWarnings"];
export type AppTransferCreateResponse = components["schemas"]["AppTransferCreateResponse"];
export type AppTransferListResponse = components["schemas"]["AppTransferListResponse"];

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

/**
 * GET /v1/apps
 * List all apps the authenticated user can access.
 */
export function listApps(): Promise<{ apps: App[] }> {
  return api.get<{ apps: App[] }>("/v1/apps");
}

export function createApp(body: AppCreationRequest): Promise<{ app: App }> {
  return api.post<{ app: App }>("/v1/apps", body);
}

/**
 * DELETE /v1/apps/:appName
 */
export function deleteApp(appName: string): Promise<void> {
  return api.delete<void>(`/v1/apps/${encodeURIComponent(appName)}`);
}

/**
 * PATCH /v1/apps/:appName
 * Rename an app.
 */
export function renameApp(appName: string, newName: string): Promise<{ app: App }> {
  return api.patch<{ app: App }>(`/v1/apps/${encodeURIComponent(appName)}`, { name: newName });
}

/**
 * GET /v1/apps/:appName/collaborators
 * Also returns the caller's capabilities on servers with app RBAC.
 */
export function getCollaborators(appName: string): Promise<CollaboratorMapResponse> {
  return api.get<CollaboratorMapResponse>(`/v1/apps/${encodeURIComponent(appName)}/collaborators`);
}

/**
 * POST /v1/apps/:appName/collaborators
 * Add a collaborator by email. The account must already exist. Servers with
 * app RBAC honor the optional role; older servers ignore it.
 */
export function addCollaborator(
  appName: string,
  email: string,
  permission?: AssignableRole,
): Promise<void> {
  return api.post<void>(
    `/v1/apps/${encodeURIComponent(appName)}/collaborators`,
    permission ? { email, permission } : { email },
  );
}

/**
 * PATCH /v1/apps/:appName/collaborators/:email
 * Change an existing collaborator's role. The owner's role can only change
 * through an ownership transfer.
 */
export function updateCollaborator(
  appName: string,
  email: string,
  permission: AssignableRole,
): Promise<void> {
  return api.patch<void>(
    `/v1/apps/${encodeURIComponent(appName)}/collaborators/${encodeURIComponent(email)}`,
    { permission },
  );
}

/**
 * DELETE /v1/apps/:appName/collaborators/:email
 */
export function removeCollaborator(appName: string, email: string): Promise<void> {
  return api.delete<void>(
    `/v1/apps/${encodeURIComponent(appName)}/collaborators/${encodeURIComponent(email)}`,
  );
}

/**
 * POST /v1/apps/:appName/transfers
 * Transfer ownership to a registered account in the same workspace.
 * The previous owner stays on as a collaborator.
 */
export function transferApp(appName: string, email: string): Promise<void> {
  return api.post<void>(`/v1/apps/${encodeURIComponent(appName)}/transfers`, { email });
}

/**
 * POST /v1/app-transfers
 * Create a pending cross-workspace transfer. Soft warnings may be set; accept
 * is what hard-fails on quota or name conflict.
 */
export function createAppTransfer(
  appName: string,
  email: string,
): Promise<AppTransferCreateResponse> {
  return api.post<AppTransferCreateResponse>("/v1/app-transfers", { appName, email });
}

/**
 * GET /v1/app-transfers
 * Pending inbound and outbound cross-workspace transfers for this session.
 */
export function listAppTransfers(): Promise<AppTransferListResponse> {
  return api.get<AppTransferListResponse>("/v1/app-transfers");
}

/**
 * POST /v1/app-transfers/:transferId/accept
 */
export function acceptAppTransfer(transferId: string): Promise<void> {
  return api.post<void>(`/v1/app-transfers/${encodeURIComponent(transferId)}/accept`);
}

/**
 * DELETE /v1/app-transfers/:transferId
 */
export function cancelAppTransfer(transferId: string): Promise<void> {
  return api.delete<void>(`/v1/app-transfers/${encodeURIComponent(transferId)}`);
}
